import { useAnalytics, useTrack } from 'use-analytics'
import { isDevelopmentEnvironment } from '../environment-utils'
import { logger } from '../logging'
import { ANALYTICS_STUB } from './analytics-stub'
import type { TypedAnalytics, TypedTrackFn } from './analytics-types'

export type { TypedAnalytics, TypedTrackFn } from './analytics-types'
export { ANALYTICS_STUB } from './analytics-stub'

let memoizedAnalytics: TypedAnalytics | undefined
let memoizedTrackFn: TypedTrackFn | undefined

export function useSafeAnalytics(): TypedAnalytics {
  try {
    // NOTE - the useAnalytics() call throws an error in a test environment.
    const analytics = useAnalytics()

    if (analytics && !memoizedAnalytics) memoizedAnalytics = analytics as TypedAnalytics
    if (memoizedAnalytics) return memoizedAnalytics

    throw new Error('Failed to load analytics module.')
  } catch (err: unknown) {
    logger().warn('Failed to load analytics. Falling back to stubbed analytics module.', { err })
    return ANALYTICS_STUB
  }
}

export function useSafeTrack(): TypedTrackFn {
  try {
    // NOTE - the useTrack() call throws an error in a test environment.
    const track = useTrack()

    if (isDevelopmentEnvironment()) return ANALYTICS_STUB.track

    if (track && !memoizedTrackFn) {
      memoizedTrackFn = async (eventName, payload, options, cb) => {
        try {
          await track(eventName, payload, options, cb)
        } catch (err) {
          logger().debug('Tracking call failed. Tracking is likely blocked by the client browser.', {
            eventName,
            payload,
            options,
          })
        }
      }
    }
    if (memoizedTrackFn) return memoizedTrackFn

    throw new Error('Failed to load analytics track function.')
  } catch (err: unknown) {
    logger().warn('Failed to load analytics track function. Falling back to stubbed track function.', { err })
    return ANALYTICS_STUB.track
  }
}
